import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { useOfferPipeline } from "@/hooks/use-offer-pipeline";
import { calcOfferTotals, type OfferLineForCalc } from "@/lib/offer-calc";
import {
  advisorPipes,
  pipeTotals,
  type AdvisorPipe,
  type PipeOffer,
  type PipeTotals,
} from "@/lib/offers-in-pipe";

/** Kolonnene `calcOfferTotals` trenger for å regne GEEK. */
const LINE_COLS =
  "offer_id, phase, cost_type, pricing_type, quantity, unit, unit_price, purchase_price, amount, overtime_split";

type LineRow = OfferLineForCalc & { offer_id: string };

export interface OffersInPipeData {
  pipes: AdvisorPipe[];
  totals: PipeTotals;
  isLoading: boolean;
}

/**
 * VIB-2094: tilbud i pipe per rådgiver, med GEEK.
 *
 * Bygger på `useOfferPipeline()` for selve tilbudene, slik at widgeten og
 * KPI-kortet alltid ser samme sett — inkludert TEST_USER_EMAIL-filteret og
 * utløpsregelen.
 *
 * GEEK må regnes, ikke leses: `offers` har ingen lagret GEEK-kolonne. Vi henter
 * derfor linjene for tilbudene som faktisk er i pipe, i ÉN spørring med
 * `.in("offer_id", …)`, og kjører `calcOfferTotals` per tilbud. Det er den
 * kanoniske kilden per docs/kpi-source-of-truth.md → «GEEK estimert (tilbud)»,
 * samme funksjon som tilbudseditorens egen GEEK-visning.
 */
export function useOffersInPipe(enabled: boolean): OffersInPipeData {
  const { offers, weights, today, isLoading: pipelineLoading } = useOfferPipeline();

  // Kun tilbud som er ute nå. Det holder linje-spørringen liten, og det er
  // uansett de eneste `advisorPipes` slipper gjennom.
  const sentOffers = offers.filter((o) => o.status === "sent");
  const offerIds = sentOffers.map((o) => o.id);

  const { data: linesByOffer, isLoading: linesLoading } = useQuery({
    queryKey: ["offer-lines-in-pipe", offerIds.join(",")],
    enabled: enabled && offerIds.length > 0,
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<Map<string, OfferLineForCalc[]>> => {
      const { data, error } = await supabase
        .from("offer_lines")
        .select(LINE_COLS)
        .in("offer_id", offerIds);
      if (error) throw error;

      const map = new Map<string, OfferLineForCalc[]>();
      for (const row of (data ?? []) as LineRow[]) {
        const { offer_id, ...line } = row;
        const bucket = map.get(offer_id);
        if (bucket) bucket.push(line);
        else map.set(offer_id, [line]);
      }
      return map;
    },
  });

  if (!enabled) {
    return { pipes: [], totals: emptyTotals(), isLoading: false };
  }

  // Uten linjene ville calcOfferTotals gitt GEEK 0 for hvert tilbud. Å vise en
  // pipe full av nuller er verre enn å vise ingenting, så vi holder tilbake til
  // linjene er der. Tomt resultat mens vi laster, med isLoading satt, så
  // widgeten viser skjelettet og aldri et tall som er feil.
  const linesReady = offerIds.length === 0 || !!linesByOffer;
  if (pipelineLoading || !linesReady) {
    return { pipes: [], totals: emptyTotals(), isLoading: true };
  }

  const pipeOffers: PipeOffer[] = sentOffers.map((o) => ({
    id: o.id,
    title: o.title,
    customerName: o.customer?.name ?? o.customer_name ?? null,
    status: o.status,
    probability: o.probability,
    valid_until: o.valid_until,
    expected_close_date: o.expected_close_date,
    sent_at: o.sent_at,
    // Linjene er garantert lastet her — guarden over returnerer tidlig ellers.
    bf: calcOfferTotals(o, linesByOffer?.get(o.id) ?? []).bf,
    created_by: o.created_by,
    writers: o.writers ?? [],
  }));

  const pipes = advisorPipes(pipeOffers, weights, today);

  return {
    pipes,
    totals: pipeTotals(pipes),
    isLoading: pipelineLoading || linesLoading,
  };
}

function emptyTotals(): PipeTotals {
  return {
    bfKr: 0,
    weightedBfKr: 0,
    offerCount: 0,
    uncategorizedCount: 0,
    missingCloseDateCount: 0,
  };
}
